import {Set} from 'immutable';
import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';

type EliminatedOptionsSet = Set<string>;

interface OptionEliminatorState {
  /**
   * This map is meant to hold the options that are eliminated for a given
   * question, where the key is the questionId and the value is a shape that
   * is dictated by the question type. E.g. each question type may implement
   * its own shape for the eliminated options.
   */
  questions: Record<string, EliminatedOptionsSet>;
}

interface OptionEliminatorActions {
  getQuestionState: (questionId: string) => EliminatedOptionsSet | undefined;
  updateQuestionState: (
    questionId: string,
    updateFunc: (state: EliminatedOptionsSet) => EliminatedOptionsSet
  ) => void;
  reset: () => void;
}

export const useOptionEliminatorStore = create<OptionEliminatorState & OptionEliminatorActions>()(
  devtools(
    immer((set, get) => ({
      questions: {},

      getQuestionState: (questionId: string) => get().questions[questionId],

      updateQuestionState: (
        questionId: string,
        updateFunc: (state: EliminatedOptionsSet) => EliminatedOptionsSet
      ) => {
        set(
          (state) => {
            state.questions[questionId] = updateFunc(state.questions[questionId] ?? Set());
          },
          false,
          'updateQuestionState'
        );
      },

      reset: () => {
        set({questions: {}}, false, 'reset');
      }
    })),
    {name: 'OptionEliminatorStore'}
  )
);

/**
 * Hook that resets the option eliminator store on mount and unmount.
 * Use this in views that need isolated option eliminator state.
 */
export function useOptionEliminatorReset() {
  const reset = useOptionEliminatorStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}
